/**
 * MODAL: CREATE CONTACT
 * Modal pour créer un nouveau contact
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useUsers } from '../../contexts/UsersContext';
import { useOrigins } from '../../hooks/useOrigins';
import { useProductsSelect } from '../../hooks/useProductsSelect';
import { useSchoolsSelect } from '../../hooks/useSchoolsSelect';
import type { CreateContactInput } from '../../schemas/contact';
import { createContactSchema } from '../../schemas/contact';
import { COUNTRIES } from '../../types/school';
import { Checkbox } from '../ui/checkbox';
import { Form, FormInput, FormTextarea, FormPhoneInput, FormSelect } from '../ui';
import { Modal, ModalFooter } from '../ui/modal';

interface CreateContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Retourner `false` garde la modale ouverte (ex: email en doublon) ;
  // tout autre retour (void/true) ferme la modale après succès.
  onSubmit: (data: CreateContactInput) => Promise<void | boolean>;
}

export function CreateContactModal({ isOpen, onClose, onSubmit }: CreateContactModalProps) {
  const { products } = useProductsSelect();
  const { schools } = useSchoolsSelect();
  const { origins } = useOrigins();
  const { usersMap } = useUsers();
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);

  const form = useForm<CreateContactInput>({
    resolver: zodResolver(createContactSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      notes: '',
      product_ids: [],
      school_id: '',
      assigned_to: '',
      origin_id: '',
      // Adresse
      address: '',
      city: '',
      postal_code: '',
      country: 'FRA',
      // Statut étudiant
      is_not_student: false,
      socio_professional_category: '',
      // Newsletter
      newsletter_opt_in: true,
    },
  });

  // Observer les champs booléens pour le rendu conditionnel
  const isNotStudent = useWatch({ control: form.control, name: 'is_not_student' });
  const newsletterOptIn = useWatch({ control: form.control, name: 'newsletter_opt_in' });

  const handleClose = () => {
    form.reset();
    setSelectedProducts([]);
    onClose();
  };

  const handleFormSubmit = async (data: CreateContactInput) => {
    const submitData = {
      ...data,
      product_ids: selectedProducts,
      school_id: data.school_id || undefined,
      assigned_to: data.assigned_to || undefined,
      origin_id: data.origin_id || undefined,
    };
    const result = await onSubmit(submitData);
    // `false` => échec métier (ex: doublon email) : on garde la modale ouverte
    // pour que l'utilisateur corrige sans reperdre sa saisie.
    if (result !== false) {
      handleClose();
    }
  };

  // Toggle produit
  const toggleProduct = (productId: string, checked: boolean) => {
    setSelectedProducts((prev) =>
      checked ? [...prev, productId] : prev.filter((id) => id !== productId)
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Créer un contact"
      size="2xl"
      footer={
        <ModalFooter
          onCancel={handleClose}
          submitLabel="Créer"
          isSubmitting={form.formState.isSubmitting}
          formId="create-contact-form"
        />
      }
    >
      <Form {...form}>
        <form id="create-contact-form" onSubmit={form.handleSubmit(handleFormSubmit)}>
          <div className="flex flex-col gap-4">
            {/* Ligne 1 : Prénom et Nom */}
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                control={form.control}
                name="first_name"
                label="Prénom"
                required
                placeholder="Ex: Léo"
              />
              <FormInput
                control={form.control}
                name="last_name"
                label="Nom"
                required
                placeholder="Ex: Usurpi"
              />
            </div>

            {/* Ligne 2 : Email et Téléphone */}
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                control={form.control}
                name="email"
                label="Email"
                type="email"
                required
                placeholder="Ex: leo.usurpi@email.com"
              />
              <FormPhoneInput
                control={form.control}
                name="phone"
                label="Téléphone"
                placeholder="6 12 34 56 78"
              />
            </div>

            {/* Section Adresse */}
            <div className="flex flex-col gap-4">
              <FormInput
                control={form.control}
                name="address"
                label="Adresse"
                placeholder="Ex: 12 rue de la Paix"
              />
              <div className="grid grid-cols-3 gap-4">
                <FormInput control={form.control} name="city" label="Ville" placeholder="Paris" />
                <FormInput
                  control={form.control}
                  name="postal_code"
                  label="Code Postal"
                  placeholder="75001"
                />
                <FormSelect
                  control={form.control}
                  name="country"
                  label="Pays"
                  options={COUNTRIES.map((c) => ({ value: c.code, label: c.label }))}
                />
              </div>
            </div>

            {/* Checkbox Plus étudiant + Inscrit à la newsletter */}
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={isNotStudent ?? false}
                    onChange={(checked) => form.setValue('is_not_student', checked)}
                  />
                  <span
                    className="text-sm text-zinc-900 cursor-pointer"
                    onClick={() => form.setValue('is_not_student', !isNotStudent)}
                  >
                    Plus étudiant
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={newsletterOptIn ?? true}
                    onChange={(checked) => form.setValue('newsletter_opt_in', checked)}
                  />
                  <span
                    className="text-sm text-zinc-900 cursor-pointer"
                    onClick={() => form.setValue('newsletter_opt_in', !(newsletterOptIn ?? true))}
                  >
                    Inscrit à la newsletter
                  </span>
                </div>
              </div>

              {isNotStudent ? (
                <FormInput
                  control={form.control}
                  name="socio_professional_category"
                  label="Catégorie socio-professionnelle"
                  placeholder="Ex: Cadre, Artisan, Profession libérale..."
                />
              ) : (
                <FormSelect
                  control={form.control}
                  name="school_id"
                  label="École"
                  placeholder="Sélectionner une école"
                  options={[
                    { value: '', label: 'Aucune' },
                    ...schools.map((s) => ({ value: s.id, label: s.name })),
                  ]}
                />
              )}
            </div>

            {/* Produits associés - Checkboxes */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-zinc-900">Produits associés</label>
              <div className="max-h-40 overflow-y-auto p-3 border border-zinc-200 rounded-md bg-zinc-50">
                {products.length === 0 ? (
                  <p className="text-sm text-zinc-500">Aucun produit disponible</p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {products.map((product) => (
                      <div
                        key={product.id}
                        className="flex items-center gap-2 hover:bg-zinc-100 p-2 rounded transition-colors"
                      >
                        <Checkbox
                          checked={selectedProducts.includes(product.id)}
                          onChange={(checked) => toggleProduct(product.id, checked)}
                        />
                        <span
                          className="text-sm text-zinc-900 cursor-pointer flex-1"
                          onClick={() =>
                            toggleProduct(product.id, !selectedProducts.includes(product.id))
                          }
                        >
                          {product.name}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-zinc-500">
                Sélectionnez un ou plusieurs produits à associer au contact
              </p>
            </div>

            {/* Assigner à + Origine */}
            <div className="grid grid-cols-2 gap-4">
              <FormSelect
                control={form.control}
                name="assigned_to"
                label="Assigner à"
                placeholder="Non assigné"
                options={[
                  { value: '', label: 'Non assigné' },
                  ...Object.values(usersMap).map((u) => ({
                    value: u.id,
                    label: u.full_name || u.email,
                  })),
                ]}
              />
              <FormSelect
                control={form.control}
                name="origin_id"
                label="Origine"
                placeholder="Aucune origine"
                options={[
                  { value: '', label: 'Aucune origine' },
                  ...origins.map((o) => ({ value: o.id, label: o.label })),
                ]}
              />
            </div>

            {/* Notes */}
            <FormTextarea
              control={form.control}
              name="notes"
              label="Notes"
              placeholder="Notes générales sur le contact..."
              rows={4}
            />
          </div>
        </form>
      </Form>
    </Modal>
  );
}
